import { useContext, useState } from 'react'
import { useLoanInfoStore } from 'store'
import PageWrapper from 'components/PageWrapper'
import Card from 'components/Card'
import { DelayIcon, InformationIcon, SimpleArrowIcon } from 'assets/icons'
import { toLocaleCurrency } from 'utils/dinero'
import { URL_AUTOGESTION } from 'config'
import { ADVANCE_STATES, CREDIT_TYPES } from 'constants/credit'
import { MeContext } from 'contexts/me'
import { ProgressBar } from 'components/AdvanceOffer'
import { TextDivider } from 'components/Divider'
import PrimaryButton from 'components/primaryButton'
import InstallmentCard from './components/InstallmentCard'
import DetailModal from './components/DetailModal'

const { PREAPPROVED, DRAFT, SEND, FINISHED } = ADVANCE_STATES
const { LOAN } = CREDIT_TYPES

const LoanDetail = () => {
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false)
  const { loanInfo } = useLoanInfoStore()

  const { me } = useContext(MeContext)

  const remainingAmount = loanInfo.installments.reduce(
    (prevValue, currValue) => prevValue + currValue.remaining_amount,
    0
  )

  const isPending =
    loanInfo.state === PREAPPROVED ||
    loanInfo.state === DRAFT ||
    loanInfo.state === SEND

  return (
    <PageWrapper
      viewSelected='/adelantos'
      title={`${loanInfo.type === LOAN ? 'Préstamo' : 'Adelanto'} #${
        loanInfo.id
      }`}
      noTabBar
    >
      <section className='text-black mt-2 sm:max-w-md pb-6 mx-auto h-full'>
        {isPending ? (
          <PendingCredit
            amount={loanInfo.delivered_amount}
            type={loanInfo.type === LOAN ? 'Préstamo' : 'Adelanto'}
          />
        ) : (
          <>
            <InfoCard
              type={loanInfo.type}
              amount={loanInfo.delivered_amount}
              remainingAmount={remainingAmount}
              installmentsCount={loanInfo.installments_count}
              paidInstallmentCount={loanInfo.payed_installments_count}
              amountToRepay={loanInfo.repayable_amount}
              setModalOpen={setIsDetailModalOpen}
              state={loanInfo.state}
            />
            <div className='my-8'>
              <TextDivider text='Cuotas' />
            </div>
            {loanInfo.installments.map((i, idx) => (
              <InstallmentCard installmentData={i} key={idx} className='mb-3' />
            ))}
            {loanInfo.state !== FINISHED && (
              <PrimaryButton
                buttonText='Pagar'
                className='mt-8'
                primaryButtonFunction={() => {
                  if (loanInfo.type === LOAN) {
                    window.open(
                      `${URL_AUTOGESTION}${me.profile.identification_number}/prestamo/${loanInfo.slug}`
                    )
                  } else {
                    window.open(
                      `${URL_AUTOGESTION}${me.profile.identification_number}/adelantos`
                    )
                  }
                }}
              />
            )}
          </>
        )}
        <DetailModal
          isOpen={isDetailModalOpen}
          setIsOpen={setIsDetailModalOpen}
          info={loanInfo}
        />
      </section>
    </PageWrapper>
  )
}

export default LoanDetail

const InfoCard = ({
  type,
  amount,
  remainingAmount,
  installmentsCount,
  paidInstallmentCount,
  amountToRepay,
  setModalOpen,
  state
}) => (
  <Card className='p-3 bg-white'>
    {state === FINISHED ? (
      <p className='font-bold'>Finalizaste tu crédito</p>
    ) : (
      <>
        <p className='font-bold'>Quedan por pagar</p>
        <p className='font-bold text-4xl'>
          ${toLocaleCurrency(remainingAmount)}
        </p>
      </>
    )}
    <ProgressBar
      className='my-2'
      value={paidInstallmentCount}
      maxValue={installmentsCount}
    />
    <p className='text-2xs font-medium'>
      Devolviste {paidInstallmentCount} de {installmentsCount}{' '}
      {installmentsCount > 1 ? 'cuotas' : 'cuota'}
    </p>
    <div className='py-1 rounded-lg grid grid-cols-[1fr,_min-content,_1fr] bg-gray-100 mt-4'>
      <div className='text-center flex flex-col'>
        <span className='text-gray-900 font-medium text-2xs'>Solicitaste</span>
        <span className='text-xs font-bold text-black'>
          ${toLocaleCurrency(amount)}
        </span>
      </div>
      <div className='w-[1px] bg-gray-500' />
      <div className='text-center flex flex-col'>
        <span className='text-gray-900 font-medium text-2xs'>
          {state === FINISHED ? 'Devolviste' : 'Devolvés'}
        </span>
        <span className='text-xs font-bold text-black'>
          ${toLocaleCurrency(amountToRepay)}
        </span>
      </div>
    </div>
    <hr className='my-4' />
    <button
      onClick={() => setModalOpen(true)}
      className='grid w-full grid-cols-[min-content,_1fr,_min-content] items-center'
    >
      <InformationIcon className='!stroke-green-800' />
      <span className='font-bold text-left text-xs text-purple-900 ml-2'>
        Detalles de tu {type === LOAN ? 'préstamo' : 'adelanto'}
      </span>
      <span className='rounded-full ml-auto shrink-0 flex items-center justify-center border border-gray-300 h-6 w-6'>
        <SimpleArrowIcon
          className='transition-all duration-200'
          stroke='#442E83'
        />
      </span>
    </button>
  </Card>
)

const PendingCredit = ({ amount, type }: { amount: number; type: string }) => (
  <div className='p-4 rounded-[10px] bg-white'>
    <div className='text-center p-4 rounded-lg outline outline-1 outline-yellow-900'>
      <DelayIcon className='mx-auto w-10 h-10' />
      <p className='font-bold mt-2 text-xs'>{type} en proceso</p>
      <div className='text-2xs font-medium text-gray-900 leading-[0.875rem]'>
        <p>Estamos con demoras en el servicio.</p>
        <p>
          Tu {type.toLowerCase()} de ${toLocaleCurrency(amount)} está siendo
          procesado.
        </p>
      </div>
    </div>
  </div>
)
